import type { ISendPasswordResetCmd } from "@/data/commands/auth/SendPasswordResetCmd";
import type { IUsernamePasswordAuthCmd } from "@/data/commands/auth/UsernamePasswordAuthCmd";
import { Factory } from "@/data/dao/Factory";
import { AuthenticationDao } from "@/data/dao/service/AuthenticationDao";

import { logError } from "@/shared/logging/errors";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";
import { FloatLabel } from "@/shared/ui/FloatLabel";
import { DomUtil } from "@/shared/ui/Helper";
import { Ripple } from "@/shared/ui/Ripple";

import { TastierClient } from "@/app/TastierClient";
import { EmailInput } from "@/app/elements/EmailInput";
import { PasswordInput } from "@/app/elements/PasswordInput";
import { trans } from "@/app/localization/Trans";
import { BasePopover } from "@/app/popover/BasePopover";
import { Popup } from "@/app/ui/Popup";

export class SigninPopover extends BasePopover {
    private authenticationDao = Factory.getInstance<AuthenticationDao>(AuthenticationDao);

    private $pwRow: JQuery;
    private $loginForm: JQuery;
    private $socialLogins: JQuery;
    private $pwResetBtn: JQuery;
    private $submitBtn: JQuery;

    private emailInput: EmailInput;
    private passwordInput: PasswordInput;
    public onSignupClicked?: { () };

    constructor(userEmail?: string) {
        super(trans.signin.title);

        this.$element.addClass("signin");

        this.$loginForm = $('<form class="form" />').appendTo(this.$body.addClass("scrollable"));

        const row = $('<div class="formrow" />').appendTo(this.$loginForm);
        this.emailInput = new EmailInput(true).appendTo(row);
        this.setPredefinedEmail(userEmail);

        this.$pwRow = $('<div class="formrow" />').appendTo(this.$loginForm);
        this.passwordInput = new PasswordInput(false, true).appendTo(this.$pwRow);

        this.$pwResetBtn = $('<a href="#" class="resetpw">' + trans.signin.resetPassword + "</a>").appendTo(
            this.$loginForm
        );

        this.$submitBtn = Ripple.bind(
            $('<button type="submit">' + trans.signin.login + "</button>")
                .appendTo(this.$loginForm)
                .click((e) => {
                    e.preventDefault();
                    if (this.$submitBtn.hasClass("loading")) {
                        e.stopPropagation();
                        return;
                    }

                    this.$loginForm.submit();
                })
        );

        FloatLabel.bind(this.$loginForm);

        this.$body.append('<div class="separator"><span>' + trans.signin.loginWith + "</span></div>");

        this.$socialLogins = $('<div class="sociallogins" />').appendTo(this.$body);

        Ripple.bindRippleOut($('<div class="sociallogin fb"><i class="fab fa-facebook-f"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("facebook");
            });

        Ripple.bindRippleOut($('<div class="sociallogin linkedin"><i class="fab fa-linkedin-in"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("linkedin");
            });

        Ripple.bindRippleOut($('<div class="sociallogin google"><i class="fab fa-google"></i></div>'))
            .appendTo(this.$socialLogins)
            .click((e) => {
                e.preventDefault();
                this.externalAuth("google");
            });

        $('<a href="#" class="signup">' + trans.signin.signup + "</a>")
            .appendTo(this.$body)
            .click((e) => {
                e.preventDefault();

                if (this.onSignupClicked !== undefined) this.onSignupClicked();
            });

        this.changeToLogin();
    }

    private changeToPwReset() {
        this.$pwResetBtn
            .html(trans.signin.backToLogin)
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();
                this.changeToLogin();
            });

        this.$submitBtn.html(trans.signin.resetPassword);
        this.$loginForm.unbind("submit").bind("submit", (e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSendPasswordReset();

            return false;
        });

        this.$pwRow.stop(true, false).slideUp("fast", "easeOutExpo");
        this.$socialLogins.stop(true, false).fadeOut("fast", "easeOutExpo");
        this.$body.find("> .separator, > .signup").stop(true, false).fadeOut("fast", "easeOutExpo");
        this.$head.find("> .title").html(trans.signin.resetPasswordTitle);
    }

    private changeToLogin() {
        this.$pwResetBtn
            .html(trans.signin.resetPassword)
            .unbind("click")
            .bind("click", (e) => {
                e.preventDefault();
                this.changeToPwReset();
            });

        this.$submitBtn.html(trans.signin.login);
        this.$loginForm.unbind("submit").bind("submit", async (e) => {
            e.preventDefault();
            e.stopPropagation();

            await this.onUsernamePasswordAuth();

            return false;
        });

        this.$pwRow.stop(true, false).slideDown("fast", "easeOutExpo");
        this.$socialLogins.stop(true, false).fadeIn("fast", "easeOutExpo");
        this.$body.find("> .separator, > .signup").stop(true, false).fadeIn("fast", "easeOutExpo");
        this.$head.find("> .title").html(trans.signin.title);
    }

    protected onShow() {}

    protected onHide() {
        this.destroy();
    }

    protected onDestroy() {}

    private async onUsernamePasswordAuth() {
        const data: IUsernamePasswordAuthCmd = {
            userName: this.emailInput.getValue(),
            password: this.passwordInput.getValue(),
            scope: "app",
            fingerprint: await DeviceFingerprintProvider.getFingerprint(),
        };

        let hasErrors = false;
        if (!this.emailInput.isValid()) {
            hasErrors = true;
            this.emailInput.setError(true);
        } else this.emailInput.setError(false);

        if (!this.passwordInput.isValid()) {
            hasErrors = true;
            this.passwordInput.setError(true);
        } else this.passwordInput.setError(false);

        if (hasErrors) {
            const lastErrorField = this.$loginForm.find(".has-error").first();
            DomUtil.scrollTo(this.$body, lastErrorField);
            return;
        }

        this.setIsLoading(true);
        try {
            await TastierClient.identityContext.authWithUsernamePassword(data);
            this.hide();
        } catch (ex) {
            logError(ex);
            this.setIsLoading(false);
            Popup.show(trans.signin.signinFailed);
        }
    }

    private async onSendPasswordReset() {
        const data: ISendPasswordResetCmd = {
            email: this.emailInput.getValue(),
        };

        let hasErrors = false;
        if (!this.emailInput.isValid()) {
            hasErrors = true;
            this.emailInput.setError(true);
        } else this.emailInput.setError(false);

        if (hasErrors) return;

        this.setIsLoading(true);
        try {
            await this.authenticationDao.sendPasswordReset(data);
            this.changeToLogin();
            Popup.show(trans.signin.checkYourInbox);
        } catch (ex) {
            logError(ex);
            Popup.show(trans.signin.resetFailed);
        } finally {
            this.setIsLoading(false);
        }
    }

    private async externalAuth(provider: string) {
        try {
            await TastierClient.identityContext.authExternal(provider);
            this.hide();
        } catch (ex) {
            logError(ex);
            this.hide();
            Popup.show(trans.signin.externalAuthFailed);
        }
    }

    private setPredefinedEmail(userEmail: string | undefined) {
        if (userEmail != null) {
            this.emailInput.setValue(userEmail);
        }
    }

    public getInputEmail(): string | undefined {
        return this.emailInput.isValid() ? this.emailInput.getValue() : undefined;
    }

    private setIsLoading(isLoading: boolean) {
        if (isLoading) {
            this.$submitBtn.prop("disabled", true).addClass("loading");
        } else {
            this.$submitBtn.prop("disabled", false).removeClass("loading");
        }
    }
}
